/**
 * Tooltip Content Definitions
 *
 * Central location for all tooltip content (simple and advanced)
 */

// ============================================
// NODE TYPES - Palette Tooltips
// ============================================

export const NODE_TOOLTIPS = {
  'upload-files': {
    simple: 'Upload COBOL source code files for analysis',
    advanced: 'Uploads source files via POST /ingest/upload. Accepts .zip, .tar, .gz. Returns source_hash for tracking files through the pipeline. Catalogs files and generates type mappings.',
  },

  'code-analysis': {
    simple: 'Analyze COBOL code and generate Java',
    advanced: 'Executes full COBOL analysis via POST /codeanalysis. Generates AST, call graphs, complexity metrics, and initial Java conversion. Produces JSON artifacts and PNG visualizations.',
  },

  'code-refactor': {
    simple: 'Modernize generated Java code',
    advanced: 'Analyzes generated Java for modernization opportunities via POST /coderefactor. Applies refactoring recipes (Spring Boot, streams, patterns). Uses hybrid rules + AI approach.',
  },

  'dependency-mapper': {
    simple: 'Map dependencies between programs',
    advanced: 'Analyzes program dependencies via POST /dependencymapper. Supports COBOL or Java source. Generates dependency graphs, identifies circular dependencies, and calculates coupling metrics.',
  },

  'monolith-identifier': {
    simple: 'Detect monolithic patterns',
    advanced: 'Identifies monolithic anti-patterns via POST /monolithidentifier. Detects God Classes, tight coupling, spaghetti code. Generates decomposition strategy for microservices.',
  },

  'data-analysis': {
    simple: 'Analyze COBOL data structures',
    advanced: 'Analyzes COBOL data structures via POST /dataanalysis. Parses copybooks, generates ERD diagrams, identifies data lineage, and recommends database schemas.',
  },

  'discovery': {
    simple: 'Generate executive analysis and ROI',
    advanced: 'Runs comprehensive discovery analysis via POST /discovery/analyze. Generates executive summary, ROI calculations, risk assessment, migration roadmap with timeline and cost estimates.',
  },

  'architecture': {
    simple: 'Recommend AWS architecture',
    advanced: 'Generates evidence-based AWS architecture recommendations via POST /architecture/analyze. Produces architecture diagrams, IaC templates (Terraform, CloudFormation), and cost estimates.',
  },

  'java-packaging': {
    simple: 'Package Java into deployable application',
    advanced: 'Creates production-ready Spring Boot package via POST /java-packaging/start. Generates Maven/Gradle build files, application.properties, Docker configs, and deployment scripts.',
  },

  'service-generation': {
    simple: 'Generate Spring Boot services from monolith',
    advanced: 'Generates real Spring @Service classes via POST /servicegeneration. Extracts methods from Code Analysis monolith into proper services using AI recommendations and ERD. NO stubs - preserves all business logic with full COBOL traceability.',
  },

  'test-stub': {
    simple: 'Generate basic JUnit test stubs',
    advanced: 'Generates JUnit test stubs via POST /test-generation/stubs. Fast, rules-based approach. Creates basic test structure without AI, suitable for quick scaffolding.',
  },

  'test-smart': {
    simple: 'Generate AI-powered JUnit tests',
    advanced: 'Generates intelligent JUnit tests via POST /test-generation/smart. Uses AI to understand business logic and generate meaningful assertions, edge cases, and mocks.',
  },

  'loop': {
    simple: 'Repeat workflow steps',
    advanced: 'Control flow node that repeats child nodes based on condition or iteration count. Useful for batch processing or iterative refinement.',
  },

  'conditional': {
    simple: 'Branch workflow based on condition',
    advanced: 'Control flow node that evaluates condition and routes execution to different paths. Supports JavaScript expressions accessing workflow context.',
  },

  'human-approval': {
    simple: 'Pause for manual review',
    advanced: 'Workflow checkpoint that pauses execution for human review. Useful before expensive operations or after critical analysis steps. Resume via workflow controls.',
  },

  'view-analyze': {
    simple: 'View code analysis results',
    advanced: 'Opens analysis view with metrics, graphs, and generated Java. Shows complexity metrics, call graphs, data flow diagrams, and side-by-side COBOL-Java comparison.',
  },

  'view-map': {
    simple: 'View dependency map',
    advanced: 'Opens interactive dependency map view. Visualizes program dependencies, call hierarchies, and coupling metrics. Supports filtering and graph exploration.',
  },

  'view-decompose': {
    simple: 'View monolith decomposition',
    advanced: 'Opens decomposition strategy view. Shows identified anti-patterns, suggested service boundaries, and migration sequence with risk assessment.',
  },

  'view-data': {
    simple: 'View data analysis results',
    advanced: 'Opens data analysis view with ERD diagrams, copybook structures, data lineage graphs, and database schema recommendations.',
  },

  'view-discovery': {
    simple: 'View discovery report',
    advanced: 'Opens executive discovery view with ROI calculations, risk assessment, migration roadmap, timeline, cost estimates, and business case summary.',
  },

  'view-architect': {
    simple: 'View architecture recommendations',
    advanced: 'Opens architecture recommendations view. Shows AWS service selection rationale, architecture diagrams, IaC templates, and cost projections.',
  },

  'view-code-editor': {
    simple: 'Edit Java code',
    advanced: 'Opens Monaco-based code editor. Supports syntax highlighting, IntelliSense, real-time validation, and side-by-side COBOL comparison. Changes saved to workspace.',
  },

  'view-refactor-report': {
    simple: 'View refactoring recommendations',
    advanced: 'Opens refactoring report with modernization opportunities. Shows detected patterns, suggested recipes, before/after code samples, and estimated effort.',
  },

  // IDMS Pipeline Nodes
  'pipeline-idms-analysis': {
    simple: 'Scan COBOL programs for IDMS database usage, schemas, and data operations',
    advanced: 'Identifies every database call in your COBOL programs — what data is read, written, or updated, which schemas and copybooks are used, and how the program is structured. This is the foundation for all downstream migration steps.',
  },

  'pipeline-idms-compatibility': {
    simple: 'Check programs for compiler compatibility issues between COBOL versions',
    advanced: 'Scans each program for coding patterns that will break or behave differently under the target compiler version. Flags each issue as auto-fixable or requiring manual review, with severity and location details.',
  },

  'pipeline-cobol-version-upgrade': {
    simple: 'Upgrade COBOL source code to the target compiler version',
    advanced: 'Applies the automated fixes identified by the compatibility scan and produces upgraded source files. Generates change reports showing every modification, along with updated JCL and data operation mappings.',
  },

  'pipeline-idms-data-architecture': {
    simple: 'Map out the database schema and record relationships',
    advanced: 'Infers the full database structure from how programs access data — records, sets, areas, and their connections. Produces a visual relationship diagram for migration planning.',
  },

  'pipeline-idms-assessment': {
    simple: 'Score migration readiness and estimate effort per program',
    advanced: 'Evaluates each program across multiple dimensions — data complexity, code complexity, coupling between programs, and compatibility. Produces a readiness score, effort estimates in developer-days, risk rankings, and a recommended migration sequence.',
  },

  'pipeline-idms-sql-generation': {
    simple: 'Generate SQL database scripts from the IDMS schema',
    advanced: 'Creates complete database scripts — table definitions and stored procedures — for your target database platform. Supports SQL Server, DB2, and Oracle. Maps COBOL data types to SQL column types with proper keys and relationships.',
  },

  // Standard Pipeline Nodes
  'pipeline-upload': {
    simple: 'Upload COBOL source code files for analysis',
    advanced: 'Uploads source files for processing. Accepts ZIP, TAR, and GZ archives or individual files. Catalogs every file, identifies file types, and prepares the codebase for downstream analysis steps.',
  },

  'pipeline-code-analysis': {
    simple: 'Analyze COBOL code structure and generate initial Java',
    advanced: 'Parses COBOL source code to extract program structure, call hierarchies, complexity metrics, and data relationships. Generates initial Java conversion and visual call graphs. This is the foundation for all downstream processing.',
  },

  'pipeline-refactor': {
    simple: 'Modernize generated Java using refactoring patterns',
    advanced: 'Analyzes generated Java for modernization opportunities. Applies refactoring recipes — Spring Boot patterns, Java streams, design patterns — using a hybrid rules-based and AI approach.',
  },

  'pipeline-ai-naming': {
    simple: 'Rename methods and services with meaningful Java names',
    advanced: 'Uses AI to replace cryptic COBOL-derived names with meaningful Java names. Renames methods in the generated classes with full traceability back to the original COBOL paragraphs.',
  },

  'pipeline-dependencies': {
    simple: 'Map dependencies between programs',
    advanced: 'Analyzes how programs call and depend on each other. Builds dependency graphs, identifies circular dependencies, and calculates coupling metrics to inform migration sequencing.',
  },

  'pipeline-monolith': {
    simple: 'Detect monolithic patterns and recommend decomposition',
    advanced: 'Identifies monolithic anti-patterns — tightly coupled code, oversized classes, tangled logic. Generates a decomposition strategy with suggested service boundaries and migration sequence.',
  },

  'pipeline-data': {
    simple: 'Analyze data structures, relationships, and schemas',
    advanced: 'Analyzes COBOL data structures — copybooks, record layouts, and file definitions. Generates entity-relationship diagrams, traces data lineage across programs, and recommends database schemas.',
  },

  'pipeline-discovery': {
    simple: 'Generate executive analysis, ROI, and migration roadmap',
    advanced: 'Runs a comprehensive discovery analysis. Produces an executive summary, ROI calculations, risk assessment, and a migration roadmap with timeline and cost estimates for stakeholder review.',
  },

  'pipeline-architecture': {
    simple: 'Recommend cloud architecture and generate deployment templates',
    advanced: 'Generates evidence-based AWS architecture recommendations. Produces architecture diagrams, infrastructure-as-code templates (Terraform, CloudFormation), and cost projections based on your workload profile.',
  },

  'pipeline-business-logic': {
    simple: 'Extract business rules and logic from COBOL programs',
    advanced: 'Analyzes COBOL business logic — ordered execution steps, data transformations, business rules, and error handling patterns. Feeds into Service Generation for richer, more accurate Java output.',
  },

  'pipeline-code-translation': {
    simple: 'Translate COBOL logic to Java method implementations',
    advanced: 'Translates COBOL paragraphs into Java method bodies using two independent approaches. Both outputs are validated for correctness and the best result is selected automatically.',
  },

  'pipeline-smart-tests': {
    simple: 'Generate intelligent JUnit tests from COBOL logic',
    advanced: 'Uses AI to understand the original business logic and generate meaningful JUnit tests. Produces real assertions, edge cases, and mock setups — not just empty test stubs.',
  },

  'pipeline-service-gen': {
    simple: 'Generate Spring Boot services from analyzed code',
    advanced: 'Generates real Spring service classes by extracting methods from the monolithic Java into properly structured services. Preserves all business logic with full traceability back to COBOL.',
  },

  'pipeline-java-analysis': {
    simple: 'Analyze generated Java for quality and structure',
    advanced: 'Evaluates generated Java classes for quality metrics — cohesion, coupling, complexity, and risk. Identifies cross-references between methods and fields, and flags areas needing improvement.',
  },

  'pipeline-code-refactor-v2': {
    simple: 'Apply automated fixes to Java quality findings',
    advanced: 'Applies automated fixes from Java Analysis — corrects naming conventions, removes dead code and unused imports, restructures methods, and improves exception handling. Includes compile validation after each change.',
  },

  'pipeline-java-validation': {
    simple: 'Validate generated Java structure and correctness',
    advanced: 'Runs mechanical repair and structural validation on generated Java before packaging. Produces downloadable reports even on failure so you can review issues.',
  },

  'pipeline-pre-flight': {
    simple: 'Compile-check Java code before packaging',
    advanced: 'Runs a compile-only check on the generated Java to catch errors before full packaging. Produces downloadable error reports on failure so you can review and fix compile issues.',
  },

  'pipeline-java-package': {
    simple: 'Package Java into a production-ready application',
    advanced: 'Creates a production-ready Spring Boot application package. Generates build configuration, application settings, Docker setup, and deployment scripts ready for your target environment.',
  },
}

// ============================================
// UI ELEMENTS - Button Tooltips
// ============================================

export const UI_TOOLTIPS = {
  runWorkflow: {
    simple: 'Execute the current workflow',
    advanced: 'Starts workflow execution in topological order. Nodes run sequentially based on dependencies. Uses abort signals for cancellation.',
  },

  pauseWorkflow: {
    simple: 'Pause workflow execution',
    advanced: 'Pauses workflow before next node starts. Current node completes. Resume via Resume button. Does not abort running operations.',
  },

  resumeWorkflow: {
    simple: 'Resume paused workflow',
    advanced: 'Resumes workflow from paused state. Continues with next node in topological order. Restores execution context.',
  },

  stopWorkflow: {
    simple: 'Stop workflow execution',
    advanced: 'Aborts workflow execution immediately. Sends abort signal to running nodes via AbortController. May leave partial results.',
  },

  saveFlow: {
    simple: 'Save workflow configuration',
    advanced: 'Saves workflow to database including node config, positions, and connections. Execution results (source_hash, job_ids) are preserved for re-runs.',
  },

  loadFlow: {
    simple: 'Load saved workflow',
    advanced: 'Loads workflow from database. Restores node config, canvas layout, and previous execution results. File uploads require re-selection.',
  },

  exportFlow: {
    simple: 'Export workflow as JSON',
    advanced: 'Exports workflow definition to JSON file. Includes nodes, edges, config, but NOT execution results or file data. Portable format for sharing.',
  },

  clearCanvas: {
    simple: 'Clear all nodes from canvas',
    advanced: 'Removes all nodes and edges from canvas. Does not affect saved flows or execution history. Prompts for confirmation if nodes exist.',
  },

  downloadArtifacts: {
    simple: 'Download generated files as ZIP',
    advanced: 'Downloads job artifacts via GET /artifacts/download/{job_id}. Creates ZIP of entire artifacts directory including JSON, graphs, and generated code.',
  },

  toggleTheme: {
    simple: 'Switch between dark and light theme',
    advanced: 'Toggles data-theme attribute. Theme preference saved to user settings. Applies CSS custom properties for consistent theming.',
  },
}

// ============================================
// CANVAS - Node State Tooltips
// ============================================

export const NODE_STATE_TOOLTIPS = {
  pending: {
    simple: 'Node is ready to run',
    advanced: 'Node is enabled and has not been executed yet. Will run when dependencies complete (or immediately if no dependencies).',
  },

  running: {
    simple: 'Node is currently executing',
    advanced: 'Node executor is running. Progress updates via ExecutionEvents.NODE_PROGRESS. Can be aborted via workflow stop.',
  },

  complete: {
    simple: 'Node completed successfully',
    advanced: 'Node execution finished. Results stored in node.data.executionResult and workflow context. Green indicator shows success.',
  },

  error: {
    simple: 'Node failed with error',
    advanced: 'Node executor threw exception. Error message in node.data.error. Red indicator shows failure. Check execution log for details.',
  },

  paused: {
    simple: 'Workflow paused at this node',
    advanced: 'Human-approval node or manual pause. Workflow waits for resume signal. Yellow indicator shows paused state.',
  },

  disabled: {
    simple: 'Node will be skipped',
    advanced: 'Node.data.enabled === false. Will be skipped during execution. Grayed out indicator shows disabled state.',
  },
}

// ============================================
// HELPER - Get Tooltip Content
// ============================================

/**
 * Get tooltip content for a given key
 * @param {string} category - NODE_TOOLTIPS, UI_TOOLTIPS, or NODE_STATE_TOOLTIPS
 * @param {string} key - Specific item key
 * @param {string} level - 'simple' or 'advanced'
 * @returns {string} Tooltip content
 */
export function getTooltipContent(category, key, level = 'simple') {
  const categoryMap = {
    node: NODE_TOOLTIPS,
    ui: UI_TOOLTIPS,
    state: NODE_STATE_TOOLTIPS,
  }

  const tooltips = categoryMap[category]
  if (!tooltips || !tooltips[key]) {
    return ''
  }

  return tooltips[key][level] || tooltips[key].simple || ''
}

export default {
  NODE_TOOLTIPS,
  UI_TOOLTIPS,
  NODE_STATE_TOOLTIPS,
  getTooltipContent,
}
